/**
 * Persisted stat-card-order wiring for a grid's `options.statCards`.
 *
 * The sibling of useStatCardVisibilityModel for the order those cards sit in:
 * a user preference holding card ids in the order the user arranged them, and
 * a stable `{ order, onChange }` object so passing it into EntityGrid doesn't
 * recreate the card state on every render.
 *
 * Contains:
 * - useStatCardOrderModel — persisted order + memoized { order, onChange }
 */

import { useMemo } from 'react';

import useGridPreferenceKey from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useUserPreference from '@sophocles/hooks/useUserPreference';

/**
 * @param {string} prefKeySuffix - fills `grid.<prefKeySuffix>.ct<customerTypeId>.statCardOrder.v1`
 * @returns {{ order: ?Array<string>, onChange: (next: Array<string>) => void }}
 *   `order` is null until the user has arranged the cards, which leaves the
 *   descriptor's own order in force.
 */
export default function useStatCardOrderModel(prefKeySuffix) {
  const prefKey = useGridPreferenceKey(prefKeySuffix, 'statCardOrder.v1');
  const [order, setOrder] = useUserPreference(prefKey, null);
  return useMemo(() => ({ order, onChange: setOrder }), [order, setOrder]);
}
